/*
 * format.js -- one place that decides how a number is rendered.
 *
 * Publishes window.fmt, because Alpine reads `fmt` as a plain global and
 * globals are not reactive: if it is not defined before Alpine's first render,
 * every binding that touches it silently keeps its fallback value forever
 * (which is exactly what happened -- the market selector rendered "BTCUSDT"
 * instead of "BTC / USDT"). boot/base.js imports this module before it calls
 * Alpine.start(), which is what guarantees the order now.
 *
 * Before this, precision was inlined at every call site, which is why the UI
 * showed `78,439.3200` for BTC and `120,000.0000` for volume: exchange payloads
 * arrive as strings like "78439.32000000" and were printed as-is.
 *
 * Everything here tolerates strings, numbers, null and NaN, and returns the
 * em-dash placeholder rather than "NaN" or "undefined" when it cannot format.
 *
 * Published on `window.fmt`, because the Alpine expressions in the templates
 * are evaluated as inline strings and can only reach globals.
 */
import { toNumber } from './number-input.js';

(function () {
  'use strict';

  const EMPTY = '–'; // en dash

  /** Accepts number | numeric string | null. Returns a finite Number or null. */
  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  function group(n, min, max) {
    return n.toLocaleString('en-US', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    });
  }

  /*
   * How many decimals a price deserves, from its own magnitude. A single fixed
   * precision cannot serve both BTC at ~78,000 and SHIB at ~0.000009, and the
   * exchange's own tick size is not available on the client for every venue.
   */
  function priceDecimals(n) {
    const a = Math.abs(n);
    if (a === 0) return 2;
    if (a >= 1000) return 2;
    if (a >= 1) return 4;
    if (a >= 0.01) return 5;
    if (a >= 0.0001) return 6;
    return 8;
  }

  /**
   * Price of an instrument. Precision adapts to magnitude.
   * `price(78439.32)` -> "78,439.32"   `price(0.00000912)` -> "0.00000912"
   */
  function price(v) {
    const n = num(v);
    if (n === null) return EMPTY;
    const d = priceDecimals(n);
    return group(n, d, d);
  }

  /**
   * Signed percentage, always with the sign and the unit, so direction survives
   * without colour. `pct(3.2612)` -> "+3.26%"   `pct(-0.9411)` -> "−0.94%"
   * Uses a real minus sign (U+2212), which aligns with digits; the ASCII hyphen
   * does not.
   */
  function pct(v, decimals = 2) {
    const n = num(v);
    if (n === null) return EMPTY;
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    return sign + group(Math.abs(n), decimals, decimals) + '%';
  }

  /**
   * Compact volume. `volume(5732)` -> "5.73K"   `volume(2482000)` -> "2.48M"
   * Below 1000 it stays exact, because small volumes are read as counts.
   */
  function volume(v) {
    const n = num(v);
    if (n === null) return EMPTY;
    const a = Math.abs(n);
    const sign = n < 0 ? '−' : '';
    const units = [
      [1e12, 'T'],
      [1e9, 'B'],
      [1e6, 'M'],
      [1e3, 'K'],
    ];
    for (const [size, suffix] of units) {
      if (a >= size) return sign + group(a / size, 2, 2) + suffix;
    }
    return sign + group(a, 0, 2);
  }

  /**
   * Indicator readings. Two to four decimals -- enough to distinguish adjacent
   * values, not enough to add noise. Oscillators bounded near 0-100 read better
   * at 2; unbounded overlays that sit near the price track the price precision.
   */
  function indicator(v) {
    const n = num(v);
    if (n === null) return EMPTY;
    const a = Math.abs(n);
    if (a >= 1000) return group(n, 2, 2);
    if (a >= 1) return group(n, 2, 4);
    return group(n, 4, 4);
  }

  /**
   * Parse a number the way the alert form writes it.
   *
   * The builder's numeric fields hold text in the user's locale (on a tr-TR
   * machine 80592 is "80.592" and 4.391 is "4,391"); lib/number-input.js owns
   * that format, and this reads it back the same way, so anything that looks at
   * the raw field text (the plain-English summary, the Studies editor) gets the
   * number the user meant. The form posts the canonical form instead
   * (alerts/main.js), so the server never has to guess a locale.
   */
  function fromLocale(v) {
    if (typeof v !== 'string') return num(v);
    return toNumber(v);
  }

  /**
   * A threshold as it reads in a sentence rather than in a column.
   *
   * `indicator()` pads to a fixed width so values line up in the chart legend;
   * that same padding turns "RSI crosses above 30" into "30.00" in prose, so
   * this drops trailing zeros while keeping thousands separators.
   */
  function threshold(v) {
    const n = num(v);
    if (n === null) return EMPTY;
    return group(n, 0, Math.abs(n) >= 1000 ? 2 : 4);
  }

  /**
   * Absolute price change, signed the same way `pct` is.
   *
   * Precision comes from `ref` -- the instrument's own price -- not from the size
   * of the move, so a −125.5 move on BTC reads "−125.50" (matching the price's
   * 2 decimals) rather than "−125.5000". Falls back to the delta itself when no
   * reference price is available.
   */
  function change(v, ref) {
    const n = num(v);
    if (n === null) return EMPTY;
    const basis = num(ref);
    const sign = n > 0 ? '+' : n < 0 ? '−' : '';
    const d = priceDecimals(basis === null ? n : basis);
    return sign + group(Math.abs(n), d, d);
  }

  /**
   * Direction as a glyph, so red/green is never the only signal.
   * Returns '', '▲' or '▼'.
   */
  function arrow(v) {
    const n = num(v);
    if (n === null || n === 0) return '';
    return n > 0 ? '▲' : '▼';
  }

  /** Tailwind text colour token for a direction. Neutral when flat or unknown. */
  function trendClass(v) {
    const n = num(v);
    if (n === null || n === 0) return 'text-ink-muted';
    return n > 0 ? 'text-up' : 'text-down';
  }

  /**
   * "3s ago" / "4m ago" / "2h ago" for the chart's data-status row.
   * `ts` is epoch milliseconds.
   */
  function since(ts, now = Date.now()) {
    const n = num(ts);
    if (n === null) return EMPTY;
    const s = Math.max(0, Math.round((now - n) / 1000));
    if (s < 60) return s + 's ago';
    if (s < 3600) return Math.round(s / 60) + 'm ago';
    if (s < 86400) return Math.round(s / 3600) + 'h ago';
    return Math.round(s / 86400) + 'd ago';
  }

  /**
   * An economic release figure, as the /economic/calendar endpoint sends it:
   * `kind` carries unit, decimals and value_kind.
   *   econ(3.4, null, {unit:'PERCENT', decimals:1})              -> "3.4%"
   *   econ(3.75, 3.5, {unit:'PERCENT', decimals:2, value_kind:'RANGE'}) -> "3.50–3.75%"
   *   econ(162, null, {unit:'THOUSANDS', decimals:0})            -> "162K"
   *   econ(7271, null, {unit:'THOUSANDS', decimals:0})           -> "7.27M"
   *   econ(9.86, null, {unit:'USD_BN', decimals:2})              -> "$9.86bn"
   *   econ(116244, null, {unit:'BTC', decimals:0})               -> "116,244 BTC"
   * Decimals are the source's own rounding, so 2.9 never becomes 2.90.
   */
  function econ(v, low, kind) {
    const n = num(v);
    if (n === null) return EMPTY;
    const k = kind || {};
    const d = Number.isInteger(k.decimals) ? k.decimals : 1;
    if (k.unit === 'USD_BN') return '$' + group(n, d, d) + 'bn';
    if (k.unit === 'USD') return '$' + group(n, d, d);
    if (k.unit === 'BTC' || k.unit === 'ETH') return group(n, d, d) + ' ' + k.unit;
    if (k.unit === 'THOUSANDS') {
      return Math.abs(n) >= 1000 ? group(n / 1000, 2, 2) + 'M' : group(n, d, d) + 'K';
    }
    let text = group(n, d, d);
    const l = num(low);
    if (k.value_kind === 'RANGE' && l !== null) text = group(l, d, d) + EMPTY + text;
    return k.unit === 'PERCENT' ? text + '%' : text;
  }

  /*
   * Quote assets, longest first so BTCUSDT splits on USDT rather than on the
   * shorter USD-family entries. Only used for display -- nothing here decides
   * what gets sent to an exchange.
   */
  const QUOTES = [
    'USDT', 'USDC', 'BUSD', 'FDUSD', 'TUSD', 'USDP', 'DAI',
    'BTC', 'ETH', 'BNB', 'SOL', 'XRP',
    'EUR', 'TRY', 'GBP', 'JPY', 'BRL', 'AUD', 'USD',
  ];

  /**
   * "BTCUSDT" -> "BTC / USDT" for the market selector, which reads as a pair
   * rather than an opaque ticker string.
   *
   * Symbols that already carry a separator are normalised to the same shape.
   * Anything unrecognised is returned untouched -- a wrong split would be worse
   * than no split.
   */
  function pair(symbol) {
    if (!symbol) return EMPTY;
    const s = String(symbol).toUpperCase().trim();

    const sep = s.match(/^([A-Z0-9]+)[\/\-_:]([A-Z0-9]+)$/);
    if (sep) return sep[1] + ' / ' + sep[2];

    for (const q of QUOTES) {
      if (s.length > q.length && s.endsWith(q)) {
        return s.slice(0, s.length - q.length) + ' / ' + q;
      }
    }
    return s;
  }

  const fmt = {
    EMPTY,
    pair,
    price,
    priceDecimals,
    threshold,
    fromLocale,
    pct,
    volume,
    indicator,
    change,
    arrow,
    trendClass,
    since,
    econ,
  };

  window.fmt = fmt;

})();

// Also reachable as window.fmt: Alpine's inline expressions can only see globals.
export const fmt = window.fmt;
